import React from 'react';

import { cn } from '@/lib/utils';
import { type CredentialIdentity } from '@/sections/asset/components/tabs/credentials/credentialForms/types';
import { type IdentityError } from '@/sections/asset/components/tabs/credentials/credentialForms/validators';

export type IdentityStatus = 'ready' | 'incomplete' | 'error';

interface IdentityAccordionHeaderProps {
  identity: CredentialIdentity;
  status: IdentityStatus;
  // Per-identity validation error (null when valid). Used to derive a short,
  // muted reason shown next to the label when the identity is not ready.
  error: IdentityError | null;
}

// Status dots use the BRIGHT chariot-stroke-* family (theme-safe in both modes),
// never the muted chariot-status-* family (dark-mode-invisible).
const STATUS_DOT: Record<IdentityStatus, string> = {
  ready: 'bg-chariot-stroke-light-green',
  incomplete: 'bg-chariot-stroke-medium',
  error: 'bg-chariot-stroke-critical',
};

// reasonFor derives a short, deterministic one-line hint from a per-identity
// validation error, surfaced in the collapsed accordion header so the user can
// see what an identity still needs without expanding it. First failing field
// wins. Returns '' when the identity is valid (no error).
function reasonFor(error: IdentityError | null): string {
  if (!error) {
    return '';
  }
  if (error.loginUrl) {
    return 'needs login URL';
  }
  if (error.inputs) {
    return 'needs username & password';
  }
  if (error.label) {
    return error.label === 'Duplicate label'
      ? 'duplicate label'
      : 'needs label';
  }
  if (error.mfa) {
    return 'invalid 2FA secret';
  }
  return '';
}

// IdentityAccordionHeader renders the INNER content of one accordion identity's
// trigger: a status dot, the identity label (fallback "Untitled"), a read-only
// "elevated" privilege chip, a "· MFA" marker when a TOTP secret is set, and a
// short muted reason when the identity is incomplete/error. The chevron is
// supplied by the AccordionTrigger primitive; the remove control is rendered as
// a SIBLING of the trigger by IdentitySetManager (never nested inside this
// button — nesting interactive elements is invalid HTML).
export function IdentityAccordionHeader({
  identity,
  status,
  error,
}: IdentityAccordionHeaderProps) {
  const label = identity.label.trim() || 'Untitled';
  const reason = status === 'ready' ? '' : reasonFor(error);
  return (
    <span className="flex min-w-0 flex-1 items-center gap-2">
      <span
        className={cn('size-2 shrink-0 rounded-full', STATUS_DOT[status])}
        aria-hidden="true"
      />
      <span className="text-chariot-text-primary truncate text-sm">
        {label}
      </span>
      {identity.privilege === 'elevated' && (
        <span className="bg-chariot-background-primary text-chariot-text-secondary border-chariot-stroke shrink-0 rounded-full border px-1.5 text-xs">
          elevated
        </span>
      )}
      {identity.totpSecret !== '' && (
        <span className="text-chariot-text-secondary shrink-0 text-xs">
          · MFA
        </span>
      )}
      {reason && (
        <span className="text-chariot-text-secondary truncate text-xs">
          {reason}
        </span>
      )}
    </span>
  );
}
